import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Mail, Clock, MessageSquare, Briefcase, Handshake, HelpCircle,
  ChevronDown, ChevronUp, Loader2, Calendar, ArrowRight, ShieldCheck,
  Sparkles, Globe, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Calendar booking URL (replace with real Calendly link when ready) ─── */
const DEMO_BOOKING_URL = 'https://calendly.com/formlio/15min-demo';

/* ─── Contact channels ──────────────────────────────────────────────── */
const CONTACT_CHANNELS = [
  {
    id: 'sales',
    Icon: Briefcase,
    title: 'Sales',
    body: 'See a live demo, get a custom plan, or talk through Pro/Business tier upgrades.',
    email: 'sales@formlio.app',
    cta: 'Book a demo',
    action: 'demo',
    accent: 'from-indigo-500 to-violet-500',
    tint: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  },
  {
    id: 'support',
    Icon: HelpCircle,
    title: 'Support',
    body: 'Technical help, billing questions, or anything that needs an engineer\'s eyes.',
    email: 'support@formlio.app',
    cta: 'Email support',
    action: 'mail',
    accent: 'from-emerald-500 to-teal-500',
    tint: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  {
    id: 'partnerships',
    Icon: Handshake,
    title: 'Partnerships',
    body: 'Reseller, agency, or integration partnerships — let\'s build something together.',
    email: 'business@formlio.app',
    cta: 'Talk partnerships',
    action: 'mail',
    accent: 'from-amber-500 to-orange-500',
    tint: 'bg-amber-50 text-amber-700 ring-amber-100',
  },
];

const TEAM_SIZE_OPTIONS = [
  'Just me',
  '2 – 10',
  '11 – 50',
  '51 – 200',
  '201 – 1,000',
  '1,000+',
];

const INQUIRY_OPTIONS = [
  { value: 'sales', label: 'Sales / demo' },
  { value: 'support', label: 'Support / technical help' },
  { value: 'partnerships', label: 'Partnerships / business' },
  { value: 'other', label: 'Something else' },
];

const FAQS = [
  {
    question: 'How quickly will I get a response?',
    answer:
      'Sales inquiries: under 4 business hours. Support: within 24 hours (often faster). Partnerships: 1 – 2 business days. Real humans, no auto-replies.',
  },
  {
    question: 'Do you offer live demos?',
    answer:
      "Yes — book a 15-minute demo from the calendar above and we'll walk you through the form builder, post-submission automation, and integrations live. No slide decks, just the product.",
  },
  {
    question: 'Can I upgrade or downgrade my plan anytime?',
    answer:
      "Yes. Switch plans from your billing page in one click. Upgrades are prorated instantly; downgrades take effect at the end of your current cycle so you never lose paid days.",
  },
  {
    question: 'Is my data secure with Formlio?',
    answer:
      'All data is encrypted in transit (TLS 1.3) and at rest. Payments are PCI-DSS handled by Razorpay/Stripe (we never touch card data). You can export or delete your data anytime.',
  },
  {
    question: 'Do you offer refunds?',
    answer:
      "All paid plans come with a 14-day money-back guarantee. If Formlio isn't the right fit, email support and we'll refund the full amount — no questions asked.",
  },
  {
    question: 'Can I use my own domain for forms?',
    answer:
      'Custom domains are available on Pro and Business plans. Connect your domain and host forms under your own brand in under 5 minutes.',
  },
];

/* ─── Sub-components ────────────────────────────────────────────────── */
const Eyebrow = ({ children, tone = 'indigo' }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ring-1 text-[11px] font-bold tracking-widest uppercase ${tones[tone]}`}>
      <Sparkles className="w-3 h-3" /> {children}
    </span>
  );
};

/* ─── Page ──────────────────────────────────────────────────────────── */
export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    team_size: '',
    inquiry_type: 'sales',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(0);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const fetchRecaptchaConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/config/recaptcha`);
        const data = await response.json();
        if (data.enabled && data.site_key) {
          setRecaptchaSiteKey(data.site_key);
          setRecaptchaEnabled(true);
        }
      } catch (error) {
        console.log('reCAPTCHA config not available');
      }
    };
    fetchRecaptchaConfig();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (recaptchaEnabled && !recaptchaToken) {
      toast.error('Please complete the CAPTCHA verification');
      return;
    }
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          subject: `[${formData.inquiry_type || 'general'}] from ${formData.name}`,
          company: formData.company,
          team_size: formData.team_size,
          inquiry_type: formData.inquiry_type,
          recaptcha_token: recaptchaToken,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Thanks! We'll get back to you within 24 hours.");
        setFormData({ name: '', company: '', email: '', team_size: '', inquiry_type: 'sales', message: '' });
        setRecaptchaToken(null);
        if (recaptchaRef.current) recaptchaRef.current.reset();
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  const onChannelClick = (ch) => {
    if (ch.action === 'demo') {
      window.open(DEMO_BOOKING_URL, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = `mailto:${ch.email}`;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <PublicHeader />

      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section
        className="relative pt-16 lg:pt-24 pb-12 lg:pb-16 overflow-hidden bg-gradient-to-b from-indigo-50/50 via-white to-white"
        data-testid="contact-hero"
      >
        <div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-indigo-100/70 blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-violet-100/60 blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto px-6 relative text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Eyebrow tone="indigo">Get in touch</Eyebrow>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.55 }}
            className="mt-5 text-4xl sm:text-5xl lg:text-[3.6rem] font-bold text-slate-900 tracking-tight leading-[1.05]"
            data-testid="contact-hero-heading"
          >
            Let's build{' '}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              smarter forms
            </span>{' '}
            together.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.55 }}
            className="mt-6 text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto"
          >
            Questions about pricing, a live demo, technical help, or partnerships — pick the channel that fits and we'll get back fast.
          </motion.p>

          {/* Response expectation strip */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.55 }}
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-emerald-200 shadow-sm text-sm"
            data-testid="response-expectation"
          >
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
            </span>
            <span className="font-medium text-slate-700">
              We typically respond within <strong className="text-emerald-700">24 hours</strong>.
            </span>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACT CHANNELS ────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="contact-channels">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow tone="violet">Choose your channel</Eyebrow>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Three ways to reach us.
            </h2>
            <p className="mt-3 text-base text-slate-600">
              We route conversations to the right person from the start — no ticket bouncing.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONTACT_CHANNELS.map((ch, i) => {
              const Icon = ch.Icon;
              return (
                <motion.button
                  key={ch.id}
                  onClick={() => onChannelClick(ch)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:border-slate-300 transition-all"
                  data-testid={`channel-${ch.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${ch.accent} text-white flex items-center justify-center shadow-md mb-4`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1.5 flex items-center gap-2">
                    {ch.title}
                    <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ring-1 ${ch.tint}`}>
                      {ch.id === 'sales' ? 'Demos' : ch.id === 'support' ? 'Help' : 'Business'}
                    </span>
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{ch.body}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 group-hover:text-indigo-700 group-hover:gap-2 transition-all">
                    {ch.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                  <p className="mt-2 text-xs text-slate-400 font-mono">{ch.email}</p>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CALENDAR / DEMO BOOKING ─────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="contact-booking">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-violet-900 to-slate-900 text-white p-8 md:p-12 shadow-xl"
          >
            <div aria-hidden className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }} />

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-[11px] font-bold tracking-widest uppercase ring-1 ring-white/20 backdrop-blur">
                  <Calendar className="w-3 h-3" /> Live demo
                </span>
                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1]">
                  Book a 15-minute{' '}
                  <span className="bg-gradient-to-r from-amber-300 to-orange-200 bg-clip-text text-transparent">
                    product walkthrough
                  </span>
                  .
                </h2>
                <p className="mt-4 text-base text-indigo-100/90 leading-relaxed">
                  See the form builder, conditional logic, integrations, and post-submission automation live — no slide decks, just the product.
                  Bring your hardest form use-case and we'll build it together.
                </p>

                <ul className="mt-6 space-y-2.5 text-sm text-indigo-100">
                  {['Real product, real account', 'Bring your use-case live', 'No sales pressure'].map((b) => (
                    <li key={b} className="flex items-center gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40 flex items-center justify-center">
                        <svg className="w-3 h-3 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/></svg>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col items-stretch gap-3">
                <a
                  href={DEMO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-demo-btn"
                  className="group flex items-center justify-between gap-3 bg-white text-indigo-700 rounded-2xl px-5 py-5 shadow-2xl shadow-black/20 hover:bg-amber-50 transition-all"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
                      <Calendar className="w-5 h-5" />
                    </span>
                    <span>
                      <p className="text-xs font-bold tracking-widest uppercase text-indigo-500">Pick a slot</p>
                      <p className="text-base font-bold text-slate-900">Book your 15-min demo</p>
                    </span>
                  </span>
                  <ArrowRight className="w-5 h-5 text-indigo-600 group-hover:translate-x-1 transition-transform" />
                </a>

                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/10 ring-1 ring-white/15 backdrop-blur">
                  <Globe className="w-4 h-4 text-indigo-200 shrink-0" />
                  <p className="text-xs text-indigo-100 leading-snug">
                    Available worldwide. We'll auto-detect your timezone when you pick a slot.
                  </p>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/10 ring-1 ring-white/15 backdrop-blur">
                  <Zap className="w-4 h-4 text-amber-200 shrink-0" />
                  <p className="text-xs text-indigo-100 leading-snug">
                    Same-week availability. Most demos confirmed within 1 business hour.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FAQ (BEFORE FORM — reduces support load) ────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="contact-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <Eyebrow tone="amber">FAQ</Eyebrow>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Most-asked questions, answered.
            </h2>
            <p className="mt-3 text-base text-slate-600">
              Save a round-trip — your question might already be solved here.
            </p>
          </div>

          <div className="space-y-3">
            {FAQS.map((faq, idx) => {
              const open = expandedFaq === idx;
              return (
                <div
                  key={faq.question}
                  className={`bg-white rounded-xl border ${open ? 'border-indigo-300 shadow-md' : 'border-slate-200'} transition-all`}
                  data-testid={`faq-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedFaq(open ? -1 : idx)}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                    data-testid={`faq-toggle-${idx}`}
                  >
                    <span className="text-sm md:text-base font-semibold text-slate-900">{faq.question}</span>
                    {open
                      ? <ChevronUp className="w-5 h-5 shrink-0 text-indigo-600" />
                      : <ChevronDown className="w-5 h-5 shrink-0 text-slate-400" />}
                  </button>
                  {open && (
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FORM ────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <Eyebrow tone="emerald">Send a message</Eyebrow>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              Still want to write? We'll read every word.
            </h2>
            <p className="mt-3 text-base text-slate-600">
              Fill it out and a real human will reply within 24 hours.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 p-6 md:p-10 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Jane Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12"
                  data-testid="contact-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  placeholder="Acme Inc."
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="h-12"
                  data-testid="contact-company-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane@acme.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="team_size">Team size</Label>
                <select
                  id="team_size"
                  value={formData.team_size}
                  onChange={(e) => setFormData({ ...formData, team_size: e.target.value })}
                  className="h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="contact-team-size-input"
                >
                  <option value="">Select team size</option>
                  {TEAM_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>How can we help?</Label>
              <div className="flex flex-wrap gap-2" data-testid="contact-inquiry-type">
                {INQUIRY_OPTIONS.map((opt) => {
                  const active = formData.inquiry_type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, inquiry_type: opt.value })}
                      data-testid={`inquiry-${opt.value}`}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        active
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Tell us a bit about what you're trying to do, and the more context the faster we'll be able to help."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={6}
                className="resize-none"
                data-testid="contact-message-input"
              />
            </div>

            {recaptchaEnabled && recaptchaSiteKey && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>
            )}

            <div className="pt-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Your info is encrypted in transit and never shared.</span>
              </div>
              <Button
                type="submit"
                size="lg"
                className="h-12 px-7 rounded-full bg-indigo-600 hover:bg-[#F59E0B] text-white text-base font-semibold shadow-lg shadow-indigo-500/25 transition-all"
                disabled={sending || (recaptchaEnabled && !recaptchaToken)}
                data-testid="contact-submit-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send message <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Secondary contacts strip */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <Mail className="w-4 h-4 text-indigo-500" /> hello@formlio.app
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-500" /> Mon – Fri · 9:00 AM – 6:00 PM IST
            </span>
            <span className="inline-flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-500" /> Real humans, no auto-replies
            </span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
